import Debug from 'debug'
import type { FieldValue, FormLayoutField } from '@/types/api'

const log = Debug('gg:rules')

const visibilityCache = new Map<string, boolean>()

export function clearVisibilityCache() {
  visibilityCache.clear()
}

function normalizeValue(v: unknown): unknown {
  if (typeof v === 'string') {
    switch (v.toLowerCase()) {
      case 'true':
      case 'yes':
        return true
      case 'false':
      case 'no':
        return false
    }
  }
  return v
}

function isNumericType(type: string): boolean {
  return type === 'number' || type === 'double' || type === 'currency'
}

/**
 * Tests whether a rule matches an actual value.
 * Rules support: arrays (OR), negation ({not: ...}), numeric ranges ({min, max, gt, lt}), and direct equality.
 */
export function testRule(rule: FieldValue, type: string, actual: unknown, dependencyField?: FormLayoutField): boolean {
  if (Array.isArray(rule)) {
    return rule.some(r => testRule(r, type, actual, dependencyField))
  }

  if (Array.isArray(actual)) {
    return actual.some(a => testRule(rule, type, a, dependencyField))
  }

  if (typeof rule === 'object' && rule !== null && 'not' in rule) {
    return !testRule((rule as { not: FieldValue }).not, type, actual, dependencyField)
  }

  if (isNumericType(dependencyField?.type ?? type) && typeof rule === 'object' && rule !== null) {
    const numActual = typeof actual === 'number' ? actual : Number.parseFloat(String(actual))
    const r = rule as { min?: number; max?: number; gt?: number; lt?: number }
    if (typeof r.max === 'number' && numActual > r.max) return false
    if (typeof r.min === 'number' && numActual < r.min) return false
    if (typeof r.gt === 'number' && numActual > r.gt) return true
    if (typeof r.lt === 'number' && numActual < r.lt) return true
  }

  return normalizeValue(actual) === normalizeValue(rule)
}

/**
 * Determines if a field should be visible based on its dependencies.
 * A field with no dependencies is always visible.
 *
 * Dependency logic (matches Aurelia frontend-app):
 * - ALL dependencies must pass (AND logic)
 * - Dependencies are TRANSITIVE: if field A depends on B, and B is hidden, A is also hidden
 */
export function isFieldVisible(
  field: FormLayoutField,
  answers: Record<string, unknown>,
  allFields: FormLayoutField[],
  _visited?: Set<string>,
): boolean {
  if (!field.dependencies || field.dependencies.length === 0) {
    return true
  }

  // Prevent circular dependency infinite loops
  const visited = _visited || new Set<string>()
  if (visited.has(field.name)) return true
  visited.add(field.name)

  for (const dep of field.dependencies) {
    const depField = allFields.find(f => f.name === dep.field)

    // Transitive check: if the dependency field itself is hidden, this field is hidden too
    if (depField && !isFieldVisible(depField, answers, allFields, visited)) {
      log('field hidden: %s (dependency %s is itself hidden)', field.name, dep.field)
      return false
    }

    const actualValue = answers[dep.field]

    // AND logic: ALL dependencies must match
    if (!testRule(dep.value, field.type, actualValue, depField)) {
      log('field hidden: %s (dependency %s did not match)', field.name, dep.field)
      return false
    }
  }

  return true
}

/**
 * Page/section visibility for multi-step forms.
 * A section is visible if ANY field within it is visible.
 */
export function isSectionVisible(
  fields: FormLayoutField[],
  answers: Record<string, unknown>,
  allFields: FormLayoutField[],
): boolean {
  if (fields.length === 0) return true
  return fields.some(f => isFieldVisible(f, answers, allFields))
}

/**
 * Filters a set of fields to only the visible ones.
 */
export function getVisibleFields(
  fields: FormLayoutField[],
  answers: Record<string, unknown>,
  allFields: FormLayoutField[],
): FormLayoutField[] {
  return fields.filter(f => isFieldVisible(f, answers, allFields))
}
